import { useMemo } from "react";
import type { CountryCertificationsEntry } from "../../../../domain/models/metrics";
import { useLocalization } from "../../../../hooks/useLocalization";
import { CountryMetricRow } from "../types";
import { buildCountryMetricRows } from "../utils/countryMetricRows";

interface UseCountryCertificationsDataResult {
  rows: CountryMetricRow[];
  isEmpty: boolean;
}

/**
 * Display rows for the "Certifications per country" card.
 *
 * Data source: the `Certifications` property of the existing country-data
 * endpoint — there is no dedicated certifications endpoint.
 *
 * Unlike the sessions and hours cards, this one takes no total override: no
 * comparably-scoped total for certifications exists, and borrowing one from
 * another endpoint would let a definition, filter or freshness difference
 * surface as a factual geographic bucket. Without it the "Other" aggregate
 * reports only what the response actually exposes, and "no certifications"
 * renders the empty state — which is also what the AC requires when locations
 * are not tracked.
 *
 * Confirmed backend behaviour: like `UserSessions`/`HoursInVr` on the same
 * endpoint, `Certifications` is zero-masked below the display threshold only for
 * companies with `isUserLevelDataEnabled = false`; companies with it enabled
 * receive exact counts. That means:
 * - `isUserLevelDataEnabled = true` — fully correct, no gap. Every country's
 *   real value reaches this hook, so `revealSmallValues` shows it as-is.
 * - `isUserLevelDataEnabled = false` — the individual above-threshold rows are
 *   correct, but the "Other" aggregate and every percentage understate: a
 *   country masked to 0 is indistinguishable from a country with none, and with
 *   no authoritative total to recover the difference, the masked remainder is
 *   silently dropped rather than attributed to "Other".
 */
export const useCountryCertificationsData = (
  rawData: CountryCertificationsEntry[],
  isUserLevelDataEnabled = false,
): UseCountryCertificationsDataResult => {
  const { t } = useLocalization();

  const rows = useMemo(
    () =>
      buildCountryMetricRows({
        entries: rawData.map((entry) => ({
          country: entry.country,
          value: entry.certifications,
        })),
        revealSmallValues: isUserLevelDataEnabled,
        emptyState: "none",
        otherLabel: t("pages.dataAnalytics.internalUsage.countryUsage.other"),
        unknownLabel: t(
          "pages.dataAnalytics.internalUsage.countryUsage.unknown",
        ),
      }),
    [rawData, t, isUserLevelDataEnabled],
  );

  return {
    rows,
    isEmpty: rows.length === 0,
  };
};
